import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { countdownText, liveHolidays } from "@/lib/heroRotation";

export function AnnouncementBar({ site }: { site: any }) {
  const cfg = site?.announcements;
  const fallback = site?.theme?.announcement || "#5B2A86";
  const items = [...liveHolidays(cfg?.holidays).filter((m: any) => m.text).map((m: any) => { const c = countdownText(m); return c ? { ...m, text: `${m.text} · ${c}`, countdown: c } : m; }), ...(cfg?.items?.filter((m: any) => m.active && m.text) || (site?.announcement ? [{ text: site.announcement }] : []))];
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const sig = items.map((m: any) => m.text).join("|");
  const [closedSig, setClosedSig] = useState<string | null>(() => localStorage.getItem("sc_ann_closed"));
  const closed = !!cfg?.dismissible && closedSig === sig;
  const n = closed ? 0 : items.length;
  const secs = Math.max(2, Number(cfg?.interval) || 5);
  useEffect(() => {
    if (n < 2 || paused) return;
    const t = setTimeout(() => setI((x) => (x + 1) % n), secs * 1000);
    return () => clearTimeout(t);
  }, [i, n, paused, secs]);
  if (!n) return null;
  const cur = i % n;
  const go = (d: number) => setI((x) => (x + d + n) % n);
  return (
    <div className="relative text-white text-[11px] tracking-[0.18em] uppercase overflow-hidden transition-colors duration-500"
      style={{ background: items[cur].color || fallback }} onMouseEnter={() => cfg?.pause_on_hover !== false && setPaused(true)} onMouseLeave={() => setPaused(false)} data-testid="announcement-bar">
      <div className="flex transition-transform duration-700 ease-[cubic-bezier(.22,.61,.36,1)]" style={{ transform: `translateX(-${cur * 100}%)` }}>
        {items.map((m: any, k: number) => {
          const inner = <span className="block md:truncate leading-snug">{m.text}</span>;
          return (
            <div key={k} className={`w-full flex-none text-center ${cfg?.dismissible ? "px-14 md:px-20" : "px-9 md:px-12"} py-2.5 self-center text-[10px] md:text-[11px] tracking-[0.12em] md:tracking-[0.18em]`} aria-hidden={k !== cur} data-testid={`announcement-slide-${k}`}>
              {m.url ? (m.url.startsWith("http") ? <a href={m.url} className="hover:underline underline-offset-4">{inner}</a> : <Link to={m.url} className="hover:underline underline-offset-4">{inner}</Link>) : inner}
            </div>
          );
        })}
      </div>
      {n > 1 && cfg?.show_arrows !== false && (
        <>
          <button onClick={() => go(-1)} aria-label="Previous message" className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 opacity-70 hover:opacity-100 transition-opacity" data-testid="announcement-prev"><ChevronLeft size={14} /></button>
          <button onClick={() => go(1)} aria-label="Next message" className={`absolute ${cfg?.dismissible ? "right-8 md:right-14" : "right-2 md:right-6"} top-1/2 -translate-y-1/2 opacity-70 hover:opacity-100 transition-opacity`} data-testid="announcement-next"><ChevronRight size={14} /></button>
        </>
      )}
      {cfg?.dismissible && <button onClick={() => { localStorage.setItem("sc_ann_closed", sig); setClosedSig(sig); }} aria-label="Close announcement" className="absolute right-2 md:right-5 top-1/2 -translate-y-1/2 opacity-70 hover:opacity-100 transition-opacity" data-testid="announcement-close"><X size={14} /></button>}
    </div>
  );
}
